import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { can } from "../lib/roles";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { Icon } from "../app/Icon";
import { labelize } from "../lib/format";
import {
  listReports, resolveReport, computeAccountRisk, listDoubleBrokeringFlags, reviewDoubleBrokeringFlag,
  type Report, type RiskBand, type DoubleBrokeringFlag,
} from "../lib/moderation";

const RISK_TONE: Record<RiskBand, string> = {
  low: "border-verified/40 text-verified",
  medium: "border-warn/40 text-warn",
  high: "border-danger/50 text-danger",
};
import { usePagedView, ViewToggle, Pagination } from "../components/Paged";

const TABS = [
  { key: "reported", label: "Reported" },
  { key: "resolved", label: "Resolved" },
  { key: "dismissed", label: "Dismissed" },
];

const reasonTone: Record<string, string> = {
  spam: "text-danger", scam: "text-danger", misleading: "text-accent-strong", harassment: "text-danger", other: "text-muted",
};

export function AdminModeration() {
  const { roles } = useAuth();
  const isAdmin = can(roles, "admin_moderation");
  const [tab, setTab] = useState("reported");
  const [reports, setReports] = useState<Report[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [dbFlags, setDbFlags] = useState<DoubleBrokeringFlag[]>([]);

  async function refresh(status = tab) {
    if (!isAdmin) return;
    const r = await listReports(status);
    setReports(r.reports);
    setCounts(r.counts);
  }
  async function refreshFlags() {
    if (!isAdmin) return;
    setDbFlags((await listDoubleBrokeringFlags("open")).flags);
  }
  useEffect(() => {
    void refresh(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);
  useEffect(() => {
    void refreshFlags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function reviewFlag(f: DoubleBrokeringFlag, decision: "confirmed" | "cleared") {
    setBusy(f.id + decision);
    try {
      await reviewDoubleBrokeringFlag(f.id, decision);
      await refreshFlags();
    } finally {
      setBusy(null);
    }
  }
  const { view, page, pageCount, pageItems, setView, setPage } = usePagedView(reports, 10, "list");

  async function resolve(r: Report, resolution: "dismiss" | "remove" | "suspend") {
    setBusy(r.id + resolution);
    try {
      await resolveReport(r.id, resolution);
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  async function checkRisk(r: Report) {
    if (!r.target_account_id) return;
    setBusy(r.id + "risk");
    try {
      const { risk } = await computeAccountRisk(r.target_account_id);
      // Reflect the fresh score on every report that targets the same account.
      setReports((prev) => prev.map((x) => (x.target_account_id === r.target_account_id ? { ...x, risk } : x)));
    } finally {
      setBusy(null);
    }
  }

  if (!isAdmin) {
    return (
      <div className="grid gap-6">
        <PageHeader title="Moderation" subtitle="Admin access required." />
        <p className="font-mono text-sm text-danger">Admins only.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader title="Admin · Moderation" subtitle="Review reported content and take action." />

      {dbFlags.length > 0 && (
        <Panel title={`Double-brokering flags (${dbFlags.length})`}>
          <p className="text-sm text-muted">Freight re-posted by the party who won it, or duplicated across posters. Confirm or clear each.</p>
          <ul className="grid gap-2 border-t border-line pt-3">
            {dbFlags.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 font-mono text-sm">
                <span className="min-w-0">
                  <span className={`font-bold uppercase ${f.kind === "rebroker_by_awardee" ? "text-danger" : "text-warn"}`}>
                    {f.kind === "rebroker_by_awardee" ? "RE-BROKER" : "DUPLICATE"}
                  </span>{" "}
                  <span className="text-muted">{[f.origin, f.destination].filter(Boolean).join(" → ") || f.load_id.slice(0, 8)}</span>{" "}
                  <span className="text-[11px] text-muted">· {f.flagged_email ?? "—"}</span>
                </span>
                <span className="flex gap-1.5">
                  <Button variant="ghost" disabled={busy === f.id + "confirmed"} onClick={() => reviewFlag(f, "confirmed")}><span className="text-danger">Confirm</span></Button>
                  <Button variant="ghost" disabled={busy === f.id + "cleared"} onClick={() => reviewFlag(f, "cleared")}>Clear</Button>
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <div className="flex flex-wrap gap-1 border-b border-line">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-bold transition-colors ${tab === t.key ? "border-accent text-accent-strong" : "border-transparent text-muted hover:text-ink"}`}
          >
            {t.label} ({counts[t.key] ?? 0})
          </button>
        ))}
      </div>

      <div className="flex justify-end"><ViewToggle view={view} onChange={setView} /></div>

      {reports.length === 0 ? (
        <p className="font-mono text-sm text-muted">Nothing here — the queue is clear.</p>
      ) : (
        <ul className={view === "grid" ? "grid gap-3 xl:grid-cols-2" : "grid gap-3"}>
          {pageItems.map((r) => (
            <li key={r.id} className="grid gap-4 rounded-card border border-line bg-surface p-5 lg:grid-cols-[1fr_240px]">
              <div className="grid gap-2">
                <div className="flex items-center gap-2">
                  <Icon name="flag" className="h-5 w-5 text-danger" />
                  <span className="font-bold">{r.context ?? `${labelize(r.target_type)} report`}</span>
                </div>
                {r.detail && <p className="rounded-lg border border-line bg-surface-2 p-3 font-mono text-sm text-muted">{r.detail}</p>}
                <p className="font-mono text-xs text-muted">{labelize(r.target_type)} · {r.target_id.slice(0, 12)}</p>
              </div>

              <div className="grid content-start gap-2 border-line lg:border-l lg:pl-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-[11px]">
                  <div><span className="block uppercase tracking-widest text-muted">Reported by</span><span className="truncate text-ink">{r.reporter_email ?? "—"}</span></div>
                  <div><span className="block uppercase tracking-widest text-muted">Reason</span><span className={`font-bold uppercase ${reasonTone[r.reason] ?? "text-muted"}`}>{r.reason}</span></div>
                  <div className="col-span-2"><span className="block uppercase tracking-widest text-muted">Reported</span><span className="text-ink">{new Date(r.created_at).toLocaleString()}</span></div>
                </div>
                {/* Fraud/risk (ADR-0019) — decision-support for this report's target account */}
                {r.target_account_id && (
                  <div className="flex items-center gap-2 border-t border-line pt-2">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-muted">Risk</span>
                    {r.risk ? (
                      <span
                        className={`rounded-full border px-2 py-0.5 font-mono text-[11px] font-bold uppercase ${RISK_TONE[r.risk.band]}`}
                        title={r.risk.reasons?.map((x) => `${x.label} (+${x.points})`).join("\n") || undefined}
                      >
                        {r.risk.band} · {r.risk.score}
                      </span>
                    ) : (
                      <span className="font-mono text-[11px] text-muted">not scored</span>
                    )}
                    <button
                      type="button"
                      className="font-mono text-[11px] font-bold text-accent-strong disabled:opacity-50"
                      disabled={busy === r.id + "risk"}
                      onClick={() => checkRisk(r)}
                    >
                      {busy === r.id + "risk" ? "…" : r.risk ? "Re-check" : "Check risk"}
                    </button>
                  </div>
                )}
                {tab === "reported" ? (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button variant="ghost" disabled={busy === r.id + "dismiss"} onClick={() => resolve(r, "dismiss")}>Dismiss</Button>
                    <Button variant="ghost" disabled={busy === r.id + "remove"} onClick={() => resolve(r, "remove")}><span className="text-danger">Remove</span></Button>
                    <Button variant="ghost" disabled={busy === r.id + "suspend" || !r.target_account_id} onClick={() => resolve(r, "suspend")}>Suspend user</Button>
                  </div>
                ) : (
                  r.resolution && <span className="font-mono text-xs text-muted">Resolution: <span className="font-bold text-ink">{r.resolution}</span></span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <Pagination page={page} pageCount={pageCount} onPage={setPage} />
    </div>
  );
}
